<template>
  <div>
    <div class="grid-x">
      <div class="cell auto">
        <h2 class="text-bold">Add {{ isAsk ? 'Ask' : 'Commitment' }}</h2>
      </div>
      <div class="cell shrink">
        <a class="no-color padding-right-small" @click="$emit('expand')"><span class="fas fa-expand"></span></a>
      </div>
    </div>

    <template v-if="commitmentTypes">
      <p v-if="commitmentTypes.length === 0">
        Call Time can be used to ask for more than just contributions. You can track other types of asks and
        commitments after you <router-link :to="{ name: 'commitmentTypes' }">setup your Commitment Types.</router-link>
      </p>

      <form-form @submit="handleSubmit" :validator="$v" :submit-error="submitError" v-else>

        <fieldset>
          <form-radio-element label="Ask" :value="true" v-model="isAsk"></form-radio-element>
          <form-radio-element label="Commitment" :value="false" v-model="isAsk"></form-radio-element>
        </fieldset>

        <form-datepicker
          placeholder="Date"
          v-model="date"
          :pick-time="true"
          :right="true"
          :validator="$v.date"
          @input="dateChanged"
        ></form-datepicker>
        <form-select
          :empty="isAsk ? 'Asked for...' : 'Committed to...'"
          :options="commitmentTypeOptions"
          v-model="commitmentTypeId"
          :validator="$v.commitmentTypeId"
          @change="typeChanged" />

        <template v-if="commitmentTypeId">
          <!--Ask Result-->
          <form-group v-if="isAsk">
            <ask-result-buttons v-model="askResult"></ask-result-buttons>
          </form-group>

          <!--Commitment Value-->
          <form-group>
            <commitment-type-value-field
              :commitment-type="commitmentTypeById(commitmentTypeId)"
              v-model="commitmentValue"
              ref="commitmentValue"
              v-if="showCommitmentValue" />
          </form-group>

          <add-note-field placeholder="Add note..." :start-lines="2" v-model="note" />
        </template>

        <form-submit :validator="$v">
          Add {{ isAsk ? 'Ask' : 'Commitment' }}
        </form-submit>
      </form-form>
    </template>

    <ul class="list-padded" v-if="asksAndCommitments">
      <li v-for="{ type, record } in asksAndCommitments" :key="`${type}${record.id}`">
        <!--Ask-->
        <template v-if="type === 'ask'">
          <div>
            <strong>Asked for {{ record.commitmentType.name }}<span v-if="record.result">: {{ record.result }}</span></strong>
            <div v-if="record.note">
              {{ record.note.note }}
            </div>
          </div>
          <div class="text-muted">
            <small>
              <em>{{ record.date | dateTimeString }} | <a class="text-danger" @click="deleteCommitmentAsk(record.id)">Delete</a></em>
            </small>
          </div>
        </template>

        <!--Commitment-->
        <template v-if="type === 'commitment'">
          <div :class="{ 'text-strike': record.is_dismissed }">
            <strong>Committed to {{ record.commitmentType.name }}<span v-if="record.value">: <commitment-type-value-string :value="record.value" :commitment-type="record.commitmentType" /></span></strong>
            <div v-if="record.note">
              {{ record.note.note }}
            </div>
          </div>
          <div class="text-muted">
            <small>
              <em>
                {{ record.date | dateTimeString }} |
                <template v-if="record.is_dismissed">
                  <a @click="dismissCommitment(record.id, true)">Un-dismiss</a> |
                  <a class="text-danger" @click="deleteCommitment(record.id)">Delete</a>
                </template>
                <template v-else>
                  <a @click="dismissCommitment(record.id)">Dismiss</a>
                </template>
              </em>
            </small>
          </div>
        </template>
      </li>
    </ul>

  </div>
</template>

<script>
import { formsMixin } from '../../../mixins/forms/formsMixin'
import { required } from 'vuelidate/lib/validators'
import moment from 'moment'
import {
  addCommitmentAskMutation, addCommitmentMutation,
  commitmentTypesQuery,
  contactCommitmentsAsksQuery,
  contactCommitmentsQuery, deleteCommitmentAskMutation, deleteCommitmentMutation,
  dismissCommitmentMutation
} from '../../../graphql/commitments'
import CommitmentTypeValueString from '../../../components/Common/CommitmentTypeValueString'
import CommitmentTypeValueField from '../../../components/Common/CommitmentTypeValueField'
import AskResultButtons from '../../../components/LogCall/AskResultButtons'
import AddNoteField from '../../../components/Contacts/AddNoteField'

export default {
  components: {
    AddNoteField,
    AskResultButtons,
    CommitmentTypeValueField,
    CommitmentTypeValueString
  },
  mixins: [
    formsMixin
  ],
  props: {
    contact: Object
  },
  data () {
    return {
      isAsk: false,
      date: moment().format('YYYY-MM-DD HH:mm'),
      commitmentTypeId: null,
      commitmentValue: null,
      askResult: null,
      note: '',
      submitError: null
    }
  },
  computed: {
    asksAndCommitments () {
      // combine the records
      let asks = this.contactCommitmentAsks || []
      let commitments = this.contactCommitments || []
      // combine the records and tag them with the type and a sort key
      const combined = []
      asks.forEach(record => {
        combined.push({ type: 'ask', sort: record.date, record })
      })
      commitments.forEach(record => {
        combined.push({ type: 'commitment', sort: record.date, record })
      })
      // sort
      return this.$helpers.sortByKey(combined, 'sort', true)
    },
    commitmentTypeOptions () {
      if (!this.commitmentTypes) {
        return []
      }
      return this.commitmentTypes.map(type => {
        return { value: type.id, text: type.name }
      })
    },
    showCommitmentValue () {
      // show value on "YES" asks and all commitments
      return this.commitmentTypeId && (!this.isAsk || (this.isAsk && this.askResult === 'Yes'))
    }
  },
  validations: {
    date: { required },
    commitmentTypeId: { required }
  },
  apollo: {
    contactCommitmentAsks: {
      query: contactCommitmentsAsksQuery,
      variables () {
        return {
          contactId: this.contact.id
        }
      }
    },
    contactCommitments: {
      query: contactCommitmentsQuery,
      variables () {
        return {
          contactId: this.contact.id
        }
      }
    },
    commitmentTypes: {
      query: commitmentTypesQuery
    }
  },
  methods: {
    dateChanged () {
      // if the date is cleared, default to today's date
      if (!this.date) {
        this.date = moment().format('YYYY-MM-DD HH:mm')
      }
    },
    typeChanged () {
      this.$nextTick(() => {
        if (this.$refs.commitmentValue) {
          // focus on the value field
          this.$refs.commitmentValue.focus()
        }
      })
    },
    commitmentTypeById (id) {
      if (this.commitmentTypes) {
        return this.commitmentTypes.find(type => type.id === parseInt(id))
      }
      return null
    },
    clearFormData () {
      this.date = null
      this.commitmentTypeId = null
      this.commitmentValue = null
      this.askResult = null
      this.note = ''
      this.$v.$reset()
      this.dateChanged()
    },
    async handleSubmit () {
      this.submitError = null
      if (this.isAsk) {
        await this.addAsk()
      } else {
        await this.addCommitment()
      }
    },
    async addAsk () {
      // determine if we're adding a commitment at the same time as we're adding the ask.
      // we would do this is the ask resulted in a "YES"
      let addCommitment = this.askResult === 'Yes'
      try {
        await this.$apollo.mutate({
          mutation: addCommitmentAskMutation,
          variables: {
            contactId: this.contact.id,
            input: {
              date: this.date,
              commitment_type_id: this.commitmentTypeId,
              result: this.askResult || null
            },
            // only add a note if we're not also adding a commitment.
            // if we're adding a commitment, then the note will be added to the commitment, and this
            // will prevent us from creating two duplicate notes.
            note: addCommitment ? null : this.note.trim()
          }
        })
        // refetch
        await this.$apollo.queries.contactCommitmentAsks.refetch()
        // add the commitment?
        if (addCommitment) {
          return this.addCommitment()
        }
        // clear form
        this.clearFormData()
      } catch (e) {
        this.submitError = e
      }
    },
    async addCommitment () {
      try {
        await this.$apollo.mutate({
          mutation: addCommitmentMutation,
          variables: {
            contactId: this.contact.id,
            input: {
              date: this.date,
              commitment_type_id: this.commitmentTypeId,
              value: this.commitmentValue ? JSON.stringify(this.commitmentValue) : null
            },
            note: this.note.trim()
          }
        })
        // refetch
        await this.$apollo.queries.contactCommitments.refetch()
        // clear form
        this.clearFormData()
      } catch (e) {
        this.submitError = e
      }
    },
    async deleteCommitmentAsk (commitmentAskId) {
      if (!confirm('Are you sure that you want to delete this ASK? This cannot be undone.')) {
        return
      }
      await this.$apollo.mutate({
        mutation: deleteCommitmentAskMutation,
        variables: {
          id: commitmentAskId
        }
      })
      await this.$apollo.queries.contactCommitmentAsks.refetch()
    },
    async deleteCommitment (commitmentId) {
      if (!confirm('Are you sure that you want to delete this COMMITMENT? This cannot be undone.')) {
        return
      }
      await this.$apollo.mutate({
        mutation: deleteCommitmentMutation,
        variables: {
          id: commitmentId
        }
      })
      await this.$apollo.queries.contactCommitments.refetch()
    },
    async dismissCommitment (commitmentId, undismiss) {
      await this.$apollo.mutate({
        mutation: dismissCommitmentMutation,
        variables: {
          id: commitmentId,
          undismiss: !!undismiss
        }
      })
    }
  }
}
</script>
